import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { waitForBackendHealth } from "../service/health.api";
import { soundEngine } from "../utils/soundEngine";

export function UmbrellaOctagonSVG({ className = "h-16 w-16", spin = true }) {
  return (
    <svg
      viewBox="0 0 120 120"
      className={`${className} ${spin ? "animate-[spin_10s_linear_infinite]" : ""}`}
      aria-hidden="true"
    >
      {/* Outer Octagon */}
      <polygon
        points="38,6 82,6 114,38 114,82 82,114 38,114 6,82 6,38"
        fill="none"
        stroke="#dc2626"
        strokeWidth="3"
      />
      {/* 8 Umbrella Wedges Alternating Red and Bone-White */}
      <path d="M60,60 L38,6 L82,6 Z" fill="#dc2626" fillOpacity="0.9" />
      <path d="M60,60 L82,6 L114,38 Z" fill="#f3f4f6" fillOpacity="0.88" />
      <path d="M60,60 L114,38 L114,82 Z" fill="#dc2626" fillOpacity="0.9" />
      <path d="M60,60 L114,82 L82,114 Z" fill="#f3f4f6" fillOpacity="0.88" />
      <path d="M60,60 L82,114 L38,114 Z" fill="#dc2626" fillOpacity="0.9" />
      <path d="M60,60 L38,114 L6,82 Z" fill="#f3f4f6" fillOpacity="0.88" />
      <path d="M60,60 L6,82 L6,38 Z" fill="#dc2626" fillOpacity="0.9" />
      <path d="M60,60 L6,38 L38,6 Z" fill="#f3f4f6" fillOpacity="0.88" />
      {/* Center Hub */}
      <circle cx="60" cy="60" r="10" fill="#060608" stroke="#dc2626" strokeWidth="2.5" />
      <circle cx="60" cy="60" r="3.5" fill="#dc2626" />
    </svg>
  );
}

const WAITING_STATUS_MESSAGES = [
  "WAKING RENDER CLOUD INSTANCE",
  "BOOTSTRAPPING BACKEND NODE",
  "ALLOCATING CONTAINER MEMORY",
  "WARMING UP API ENDPOINTS",
  "ESTABLISHING SECURE HANDSHAKE",
  "SYNCING PORTFOLIO TELEMETRY",
];

function RequiemLoader({ onStartReveal, onComplete }) {
  const [progress, setProgress] = useState(0);
  const [backendReady, setBackendReady] = useState(false);
  const [attemptCount, setAttemptCount] = useState(1);
  const [statusIdx, setStatusIdx] = useState(0);
  const [dotCount, setDotCount] = useState(1);
  const [exiting, setExiting] = useState(false);
  const [glitchBurst, setGlitchBurst] = useState(false);
  const backendReadyRef = useRef(false);

  // Sync saved theme immediately so loader matches Dark, Light, or B&W mode
  useEffect(() => {
    try {
      const savedTheme = localStorage.getItem("portfolio_theme") || "dark";
      document.documentElement.setAttribute("data-theme", savedTheme);
    } catch {}
  }, []);

  // Always ping Render backend health API until the server wakes up and responds
  useEffect(() => {
    const { promise, cancel } = waitForBackendHealth({
      intervalMs: 2000,
      onAttempt: (n) => setAttemptCount(n),
    });

    promise.then(() => {
      backendReadyRef.current = true;
      setBackendReady(true);
    });

    return () => cancel();
  }, []);

  // Cycle animated dots and rotating status messages so the screen never looks frozen
  useEffect(() => {
    const dotTimer = setInterval(() => {
      setDotCount((d) => (d % 3) + 1);
    }, 380);
    const statusTimer = setInterval(() => {
      setStatusIdx((idx) => (idx + 1) % WAITING_STATUS_MESSAGES.length);
    }, 1800);
    return () => {
      clearInterval(dotTimer);
      clearInterval(statusTimer);
    };
  }, []);

  // Advance progress bar: smoothly approaches 99.9% with decimal micro-ticks while waiting, never freezing at 94
  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (backendReadyRef.current) {
          if (prev >= 100) {
            clearInterval(interval);
            return 100;
          }
          const fastStep = Math.max(4, Math.ceil((100 - prev) * 0.38));
          return Math.min(100, Math.floor(prev + fastStep));
        }

        // Fast ramp up to 72%
        if (prev < 72) {
          return Math.min(72, prev + Math.floor(Math.random() * 4) + 2);
        }
        // Medium ramp from 72% to 90%
        if (prev < 90) {
          return Math.random() > 0.42 ? prev + 1 : prev;
        }
        // Above 90%: continuous decimal micro-progression up to 99.4% (with gentle oscillation if long cold-start)
        if (prev < 99.1) {
          const remaining = 99.4 - prev;
          const microStep = Math.max(0.08, remaining * 0.035);
          return Number((prev + microStep).toFixed(1));
        }
        // If cold-start takes extra long at 99.1%, gently pulse between 96.4% and 99.3% so counter stays alive
        return 96.4;
      });
    }, 65);

    return () => clearInterval(interval);
  }, []);

  // Trigger subtle system-error jiggle burst during loading milestones and periodic waiting heartbeats
  useEffect(() => {
    if (
      (progress > 24 && progress < 32) ||
      (progress > 60 && progress < 68) ||
      progress === 100
    ) {
      setGlitchBurst(true);
      soundEngine.playTypewriter();
      const t = setTimeout(() => setGlitchBurst(false), 180);
      return () => clearTimeout(t);
    }
  }, [progress]);

  // Periodic visual pulse while waiting at >=90% so center logo also shows active life
  const isWaitingAtHighProgress = !backendReady && progress >= 90;
  useEffect(() => {
    if (!isWaitingAtHighProgress) return;
    const pulseInterval = setInterval(() => {
      setGlitchBurst(true);
      setTimeout(() => setGlitchBurst(false), 160);
    }, 2600);
    return () => clearInterval(pulseInterval);
  }, [isWaitingAtHighProgress]);

  // Only reveal page once backendReady is true AND progress reaches 100
  useEffect(() => {
    if (backendReady && progress === 100 && !exiting) {
      const startExitTimer = setTimeout(() => {
        setExiting(true);
        onStartReveal?.();
        const doneTimer = setTimeout(() => {
          onComplete?.();
        }, 850);
        return () => clearTimeout(doneTimer);
      }, 220);

      return () => clearTimeout(startExitTimer);
    }
  }, [backendReady, progress, exiting, onStartReveal, onComplete]);

  return (
    <motion.div
      initial={{ y: "0%" }}
      animate={{ y: exiting ? "-100%" : "0%" }}
      transition={{
        duration: 0.85,
        ease: [0.76, 0, 0.24, 1],
      }}
      style={{
        backgroundColor: "var(--bg-primary)",
        color: "var(--text-primary)",
        borderBottom: exiting ? "1px solid var(--border-strong)" : "none",
      }}
      className="fixed inset-0 z-[10000] flex flex-col items-center justify-between px-6 py-10 shadow-[0_28px_90px_rgba(0,0,0,0.65)] select-none"
    >
      {/* Top Spacer to keep center block dead-center */}
      <div className="h-4 w-full" aria-hidden="true" />

      {/* Center Minimalist Brand (Matches // SQUARED reference) with System-Error Jiggle */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={
          exiting
            ? { opacity: 0, y: -36, transition: { duration: 0.4, ease: "easeIn" } }
            : glitchBurst
            ? {
                opacity: [1, 0.45, 1, 0.7, 1],
                x: [0, -7, 6, -4, 3, 0],
                y: [0, -2, 2, -1, 0],
                skewX: [0, -5, 5, -2, 0],
                transition: { duration: 0.22 },
              }
            : {
                opacity: [0, 0.9, 0.4, 1],
                y: [24, -2, 1, 0],
                x: [0, -5, 4, 0],
                skewX: [0, -3, 2, 0],
                transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
              }
        }
        className="flex flex-col items-center text-center"
      >
        <span
          className="font-tech-label text-[11px] font-semibold uppercase tracking-[0.38em] sm:text-xs"
          style={{ color: "var(--text-muted)" }}
        >
          WELCOME TO
        </span>

        <div className="mt-3 flex items-center gap-3 sm:gap-4">
          <span
            className="font-orbitron text-3xl font-black italic tracking-tighter sm:text-5xl"
            style={{ color: "var(--text-primary)" }}
          >
            {"//"}
          </span>
          <h1
            className="font-orbitron text-3xl font-black uppercase tracking-[0.14em] sm:text-5xl"
            style={{ color: "var(--text-primary)" }}
          >
            HIMANSHU
          </h1>
        </div>

        {/* Prominent Animated Center Spinner & Radar Ring while Backend Wakes Up */}
        <div className="mt-8 flex flex-col items-center gap-3">
          <div className="relative flex h-16 w-16 items-center justify-center">
            {/* Outer Counter-Rotating Dashed Ring */}
            <motion.div
              className="absolute inset-0 rounded-full border-2 border-dashed"
              style={{ borderColor: "var(--text-muted)", opacity: 0.45 }}
              animate={{ rotate: -360 }}
              transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
            />
            {/* Middle High-Speed Orbiting Arc */}
            <motion.div
              className="absolute inset-1.5 rounded-full border-2 border-transparent"
              style={{
                borderTopColor: "var(--text-primary)",
                borderRightColor: "var(--accent, #dc2626)",
              }}
              animate={{ rotate: 360 }}
              transition={{
                duration: isWaitingAtHighProgress ? 0.9 : 1.35,
                repeat: Infinity,
                ease: "linear",
              }}
            />
            {/* Inner Reverse Orbiting Arc */}
            <motion.div
              className="absolute inset-3.5 rounded-full border-2 border-transparent"
              style={{
                borderBottomColor: "var(--accent, #dc2626)",
                borderLeftColor: "var(--text-primary)",
              }}
              animate={{ rotate: -360 }}
              transition={{
                duration: isWaitingAtHighProgress ? 1.1 : 1.7,
                repeat: Infinity,
                ease: "linear",
              }}
            />
            {/* Pulsing Core Dot */}
            <motion.div
              className="h-2.5 w-2.5 rounded-full"
              style={{
                backgroundColor: backendReady ? "#10b981" : "var(--accent, #dc2626)",
                boxShadow: "0 0 12px var(--accent, #dc2626)",
              }}
              animate={{ scale: [0.85, 1.35, 0.85], opacity: [0.6, 1, 0.6] }}
              transition={{ duration: 1.1, repeat: Infinity, ease: "easeInOut" }}
            />
          </div>

          {/* Animated Wave Equalizer Bars right below spinner */}
          <div className="flex h-4 items-center gap-1.5" aria-hidden="true">
            {[0, 1, 2, 3, 4, 5, 6].map((i) => (
              <motion.span
                key={i}
                className="w-1 rounded-full"
                style={{
                  backgroundColor:
                    i % 2 === 0 ? "var(--text-primary)" : "var(--accent, #dc2626)",
                }}
                animate={{
                  height: ["4px", "16px", "6px", "14px", "4px"],
                  opacity: [0.4, 1, 0.5, 0.95, 0.4],
                }}
                transition={{
                  duration: 0.9,
                  repeat: Infinity,
                  delay: i * 0.11,
                  ease: "easeInOut",
                }}
              />
            ))}
          </div>
        </div>
      </motion.div>

      {/* Bottom Animated Progress Bar + Numeric Counter + Backend Health Status */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={
          exiting
            ? { opacity: 0, y: -16, transition: { duration: 0.3 } }
            : { opacity: 1, y: 0, transition: { delay: 0.1, duration: 0.45 } }
        }
        className="flex flex-col items-center gap-3 pb-1"
      >
        <div className="flex items-center gap-4">
          {/* Active Rotating Tactical Ring / Pulse Indicator */}
          <div className="relative flex h-5 w-5 items-center justify-center">
            <motion.span
              className="absolute inset-0 rounded-full border-2 border-transparent"
              style={{
                borderTopColor: backendReady ? "#10b981" : "var(--accent, #dc2626)",
                borderRightColor: "var(--text-primary)",
              }}
              animate={{ rotate: 360 }}
              transition={{ duration: 0.85, repeat: Infinity, ease: "linear" }}
            />
            <span
              className={`h-1.5 w-1.5 rounded-full ${
                backendReady ? "bg-emerald-500" : "animate-ping bg-red-500"
              }`}
            />
          </div>

          <div
            className="relative h-1.5 w-48 overflow-hidden rounded-full sm:w-64"
            style={{ backgroundColor: "var(--border-color)" }}
          >
            {/* Base Progress Fill */}
            <motion.div
              className="h-full rounded-full"
              style={{ backgroundColor: "var(--text-primary)" }}
              initial={{ width: "0%" }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.08, ease: "linear" }}
            />

            {/* Indeterminate Continuous Sweeping Beam while waiting so bar clearly moves */}
            {!backendReady && (
              <motion.div
                className="pointer-events-none absolute inset-y-0 w-24 rounded-full"
                style={{
                  background:
                    "linear-gradient(90deg, transparent, #ef4444, #ffffff, #ef4444, transparent)",
                  boxShadow: "0 0 12px rgba(239,68,68,0.9)",
                }}
                animate={{ left: ["-40%", "110%"] }}
                transition={{
                  duration: 1.1,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
            )}
          </div>

          <span
            className="font-tech-label w-14 text-right text-xs font-semibold tabular-nums tracking-wider"
            style={{ color: "var(--text-primary)" }}
          >
            {Number.isInteger(progress) ? `${progress}%` : `${progress.toFixed(1)}%`}
          </span>
        </div>

        {/* Live Rotating Status Telemetry */}
        <div className="flex items-center gap-2.5">
          <span
            className="font-code-tagline text-[10px] uppercase tracking-[0.2em] sm:text-[11px]"
            style={{ color: "var(--text-muted)" }}
          >
            {backendReady
              ? "BACKEND SERVER ONLINE — LAUNCHING"
              : progress >= 90
              ? `${WAITING_STATUS_MESSAGES[statusIdx]} ${
                  attemptCount > 1 ? `[PING #${attemptCount}]` : ""
                }${".".repeat(dotCount)}`
              : `CONNECTING TO BACKEND SERVER${".".repeat(dotCount)}`}
          </span>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default RequiemLoader;
